import { useContext, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { toast } from "sonner";
import MerchantContext from "../../contexts/MerchantContext";
import MainSectionSection from "../../components/MainSectionSection";
import SectionTitle from "../../components/SectionTitle";
import TableContainer, { Column } from "../../components/TableContainer";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { formatDateForInput } from "../../utils/index";
import { trpc } from "@/trpc";
import type { RouterOutputs } from "@/trpc";
import { cn } from "@/lib/utils";

type StatusEvent = RouterOutputs["employees"]["listStatusEvents"][number];
type DatePreset = "hoy" | "7d" | "mes" | "30d";
type ActionFilter = "all" | "activated" | "deactivated";

const columns: Column[] = [
  { key: "timestamp", label: "Fecha", width: "200px" },
  { key: "action", label: "Acción", width: "160px" },
  { key: "employee", label: "Empleado", width: "1fr" },
  { key: "triggeredBy", label: "Realizado por", width: "1fr" },
];

const subDays = (date: Date, days: number) => {
  const d = new Date(date);
  d.setDate(d.getDate() - days);
  return d;
};

const presetRange = (preset: DatePreset) => {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  switch (preset) {
    case "hoy":
      return { start: today, end: today };
    case "7d":
      return { start: subDays(today, 6), end: today };
    case "mes":
      return { start: firstOfMonth, end: today };
    case "30d":
      return { start: subDays(today, 29), end: today };
  }
};

const StatusPill = ({ isActivation }: { isActivation: boolean }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700">
    <span
      className={cn(
        "size-1.5 rounded-full",
        isActivation ? "bg-emerald-500" : "bg-gray-400"
      )}
    />
    {isActivation ? "Activado" : "Desactivado"}
  </span>
);

const PresetChip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "h-9 rounded-full px-3.5 text-xs font-medium transition-colors",
      active
        ? "bg-palomma-primary text-white"
        : "border border-gray-primary bg-white text-gray-700 hover:bg-gray-50"
    )}
  >
    {children}
  </button>
);

const AuditoriaContainer = () => {
  const { merchantId } = useContext(MerchantContext);
  const today = new Date();

  const initialRange = presetRange("mes");
  const [startDate, setStartDate] = useState<string>(formatDateForInput(initialRange.start));
  const [endDate, setEndDate] = useState<string>(formatDateForInput(initialRange.end));
  const [activePreset, setActivePreset] = useState<DatePreset | null>("mes");
  const [actionFilter, setActionFilter] = useState<ActionFilter>("all");

  useEffect(() => {
    const previous = document.title;
    document.title = "Auditoría — Palomma";
    return () => {
      document.title = previous;
    };
  }, []);

  const applyPreset = (preset: DatePreset) => {
    const { start, end } = presetRange(preset);
    setStartDate(formatDateForInput(start));
    setEndDate(formatDateForInput(end));
    setActivePreset(preset);
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setStartDate(next);
    if (next > endDate) setEndDate(next);
    setActivePreset(null);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setEndDate(next);
    if (next < startDate) setStartDate(next);
    setActivePreset(null);
  };

  const events = trpc.employees.listStatusEvents.useQuery(
    { merchantId, startDate, endDate },
    {
      enabled: !!merchantId,
      onError: (error) => {
        toast.error("No se pudo cargar la auditoría", {
          description: error.message,
        });
      },
    }
  );

  const filteredEvents = useMemo(() => {
    if (!events.data) return [];
    if (actionFilter === "all") return events.data;
    const target =
      actionFilter === "activated" ? "employee activated" : "employee deactivated";
    return events.data.filter((e) => e.event === target);
  }, [events.data, actionFilter]);

  const renderCell = (event: StatusEvent, columnKey: string) => {
    const isActivation = event.event === "employee activated";
    switch (columnKey) {
      case "timestamp":
        return (
          <span className="text-sm text-gray-700">
            {dayjs(event.timestamp).format("DD/MM/YYYY HH:mm")}
          </span>
        );
      case "action":
        return <StatusPill isActivation={isActivation} />;
      case "employee":
        return <span>{event.properties.employee_name ?? "—"}</span>;
      case "triggeredBy":
        return (
          <span title={event.properties.triggered_by_email}>
            {event.properties.triggered_by_name ?? "—"}
          </span>
        );
      default:
        return null;
    }
  };

  const renderMobileCard = (event: StatusEvent) => {
    const isActivation = event.event === "employee activated";
    return (
      <div className="flex flex-col gap-1.5 p-3">
        <div className="flex items-center justify-between">
          <StatusPill isActivation={isActivation} />
          <span className="text-xs text-gray-700">
            {dayjs(event.timestamp).format("DD/MM/YYYY HH:mm")}
          </span>
        </div>
        <span className="font-medium">{event.properties.employee_name ?? "—"}</span>
        <span
          className="text-xs text-gray-700"
          title={event.properties.triggered_by_email}
        >
          por {event.properties.triggered_by_name ?? "—"}
        </span>
      </div>
    );
  };

  const getRowKey = (event: StatusEvent) =>
    `${event.timestamp}-${event.properties.employee_id ?? ""}-${event.event}`;

  const isFiltered = actionFilter !== "all";
  const isWaitingForMerchant = !merchantId;
  const isLoading = events.isLoading || isWaitingForMerchant;
  const isEmpty = !isLoading && filteredEvents.length === 0;

  const actionFilterOptions: Array<{ key: ActionFilter; label: string }> = [
    { key: "all", label: "Todos" },
    { key: "activated", label: "Activaciones" },
    { key: "deactivated", label: "Desactivaciones" },
  ];

  return (
    <MainSectionSection>
      <div className="flex flex-col gap-1">
        <SectionTitle title="Auditoría" />
        <p className="text-sm text-gray-700">
          Cambios en activación de empleados
        </p>
      </div>

      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="flex flex-wrap items-center gap-1.5">
          <PresetChip active={activePreset === "hoy"} onClick={() => applyPreset("hoy")}>
            Hoy
          </PresetChip>
          <PresetChip active={activePreset === "7d"} onClick={() => applyPreset("7d")}>
            Últimos 7 días
          </PresetChip>
          <PresetChip active={activePreset === "mes"} onClick={() => applyPreset("mes")}>
            Este mes
          </PresetChip>
          <PresetChip active={activePreset === "30d"} onClick={() => applyPreset("30d")}>
            Últimos 30 días
          </PresetChip>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <p className="mb-1 text-xs text-gray-700">Fecha de inicio</p>
            <Input
              type="date"
              className="h-11 w-full bg-white md:h-10 md:w-[180px]"
              value={startDate}
              onChange={handleStartDateChange}
              max={formatDateForInput(today)}
              min="2020-01-01"
            />
          </div>
          <div>
            <p className="mb-1 text-xs text-gray-700">Fecha de fin</p>
            <Input
              type="date"
              className="h-11 w-full bg-white md:h-10 md:w-[180px]"
              value={endDate}
              onChange={handleEndDateChange}
              max={formatDateForInput(today)}
              min={startDate}
            />
          </div>
        </div>
      </div>

      <div className="inline-flex w-fit rounded-full border border-gray-primary bg-gray-50 p-1">
        {actionFilterOptions.map((option) => {
          const active = actionFilter === option.key;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => setActionFilter(option.key)}
              className={cn(
                "h-7 rounded-full px-3 text-xs font-medium transition-colors",
                active
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="flex min-h-[300px] items-center justify-center">
          <Spinner size="lg" />
        </div>
      ) : isEmpty ? (
        <div className="flex min-h-[200px] flex-col items-center justify-center gap-1 rounded-lg border border-gray-primary bg-white px-4 text-center">
          <p className="text-sm font-medium text-gray-700">
            {isFiltered
              ? "Sin eventos para este filtro"
              : "Aún no hay cambios registrados"}
          </p>
          <p className="text-xs text-gray-700">
            {isFiltered
              ? "Prueba con otro filtro o rango de fechas."
              : "Cuando actives o desactives empleados en Asignación, aparecerán aquí."}
          </p>
        </div>
      ) : (
        <TableContainer
          columns={columns}
          data={filteredEvents}
          renderCell={renderCell}
          renderMobileCard={renderMobileCard}
          getRowKey={getRowKey}
          refetch={() => events.refetch()}
          pagination={false}
        />
      )}
    </MainSectionSection>
  );
};

export default AuditoriaContainer;
